package org.wordsapp.ui.component

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import org.wordsapp.model.Difficulty
import org.wordsapp.model.Word
import org.wordsapp.theme.*

@Composable
fun WordStatCard(word: Word, pickProbability: Double, nowMs: Long, modifier: Modifier = Modifier) {
    Card(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
    ) {
        BoxWithConstraints(modifier = Modifier.padding(16.dp)) {
            // Wide cards (desktop/tablet): word block on the left, stat grid on the right,
            // so the numbers sit on the same lines as the word instead of floating below it.
            if (maxWidth >= WIDE_CARD_MIN_WIDTH) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    WordHeader(word, Modifier.weight(1f))
                    Spacer(Modifier.width(16.dp))
                    StatGrid(word, pickProbability, nowMs, Modifier.width(STAT_GRID_WIDTH))
                }
            } else {
                Column(modifier = Modifier.fillMaxWidth()) {
                    WordHeader(word, Modifier.fillMaxWidth())
                    Spacer(Modifier.height(12.dp))
                    StatGrid(word, pickProbability, nowMs, Modifier.fillMaxWidth())
                }
            }
        }
    }
}

private val WIDE_CARD_MIN_WIDTH = 640.dp
private val STAT_GRID_WIDTH = 360.dp

@Composable
private fun WordHeader(word: Word, modifier: Modifier = Modifier) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = word.foreignWord,
                style = MaterialTheme.typography.titleMedium,
            )
            Text(
                text = word.translation,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        Spacer(Modifier.width(8.dp))
        DifficultyBadge(word.difficulty)
    }
}

/** Two rows of four equal-width columns so the numbers line up vertically. */
@Composable
private fun StatGrid(word: Word, pickProbability: Double, nowMs: Long, modifier: Modifier = Modifier) {
    Column(modifier = modifier) {
        Row(modifier = Modifier.fillMaxWidth()) {
            if (word.totalAttempts > 0) {
                StatItem("Correct", word.correctAnswers.toString(), Green500, Modifier.weight(1f))
                StatItem("Wrong", word.incorrectAnswers.toString(), Red500, Modifier.weight(1f))
                StatItem("Rate", "${(word.successRate * 100).toInt()}%", Blue500, Modifier.weight(1f))
            }
            StatItem("Chance", formatPercent(pickProbability), MaterialTheme.colorScheme.primary, Modifier.weight(1f))
        }

        if (word.totalAttempts > 0) {
            Spacer(Modifier.height(8.dp))
            Row(modifier = Modifier.fillMaxWidth()) {
                StatItem("Streak", word.effectiveStreak.toString(), Green500, Modifier.weight(1f))
                StatItem("Ease", format1(word.effectiveEase), Orange500, Modifier.weight(1f))
                StatItem("Due", formatDue(word, nowMs), if (word.isDue(nowMs)) Red500 else Green500, Modifier.weight(1f))
                StatItem("Weight", format1(word.weight(nowMs)), MaterialTheme.colorScheme.primary, Modifier.weight(1f))
            }
        }
    }
}

private fun format1(value: Double): String {
    val tenths = (value.coerceAtLeast(0.0) * 10 + 0.5).toLong()
    return "${tenths / 10}.${tenths % 10}"
}

// One decimal below 10% so small chances stay distinguishable from zero.
private fun formatPercent(probability: Double): String {
    val percent = probability.coerceIn(0.0, 1.0) * 100
    return if (percent >= 9.95) "${(percent + 0.5).toInt()}%" else "${format1(percent)}%"
}

private fun formatDays(days: Double): String = when {
    days < 1.0 -> "<1d"
    days < 30.0 -> "${(days + 0.5).toInt()}d"
    else -> "${(days / 30.4 + 0.5).toInt()}mo"
}

private fun formatDue(word: Word, nowMs: Long): String {
    if (word.isDue(nowMs)) return "now"
    val inDays = word.effectiveIntervalDays - word.daysSinceLastAttempt(nowMs)
    return "in ${formatDays(inDays)}"
}

@Composable
private fun DifficultyBadge(difficulty: Difficulty) {
    val (color, bgColor) = when (difficulty) {
        Difficulty.NEW -> Blue500 to Blue500.copy(alpha = 0.1f)
        Difficulty.EASY -> Green500 to Green500.copy(alpha = 0.1f)
        Difficulty.MEDIUM -> Orange500 to Orange500.copy(alpha = 0.1f)
        Difficulty.HARD -> Red500 to Red500.copy(alpha = 0.1f)
    }
    Surface(
        shape = RoundedCornerShape(8.dp),
        color = bgColor,
    ) {
        Text(
            text = difficulty.label,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
            style = MaterialTheme.typography.labelSmall,
            color = color,
        )
    }
}

@Composable
private fun StatItem(label: String, value: String, color: Color, modifier: Modifier = Modifier) {
    Column(modifier = modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        Text(
            text = value,
            style = MaterialTheme.typography.titleMedium,
            color = color,
        )
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}
